'use client';
import { useState, useEffect } from 'react';
import { ETSY_URL } from '@/data/bundles';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    setMobileOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const navItems = [
    { label: "What\'s Included", id: 'whats-included' },
    { label: 'Categories', id: 'bundles' },
    { label: 'Why This Bundle', id: 'why-bundle' },
    { label: "Who It\'s For", id: 'personas' },
    { label: 'FAQ', id: 'faq' },
  ];

  return (
    <>
      {/* Promo Bar */}
      <div className="promo-bar relative z-50 text-white text-center py-2.5 px-4">
        <div className="flex items-center justify-center gap-2 flex-wrap">
          <span className="text-sm font-medium">🔥 43 Premium Collections • One-Time Purchase • Instant Digital Access</span>
          <a
            href={ETSY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-bold underline underline-offset-2 hover:no-underline transition-all ml-1"
          >
            Get the Bundle →
          </a>
        </div>
      </div>

      {/* Header */}
      <header
        className={`fixed left-0 right-0 z-40 transition-all duration-300 ${
          scrolled ? 'nav-scrolled' : 'bg-transparent'
        }`}
        style={{ top: '2.5rem' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <a href="/" className="flex items-center gap-2.5 group">
              <img
                src="/assets/bundlevault-logo.svg"
                alt="BundleVault Logo Icon"
                width={36}
                height={36}
                className="rounded-xl shadow-lg group-hover:scale-105 transition-transform duration-200"
              />
              <img
                src="/assets/bundlevault-wordmark.svg"
                alt="Premium Bundle Vault"
                height={28}
                className="hidden sm:block h-7 w-auto group-hover:opacity-90 transition-opacity duration-200"
              />
              <span className="sm:hidden font-extrabold text-base tracking-tight" style={{ color: 'var(--foreground)' }}>
                Bundle Vault
              </span>
            </a>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollTo(item.id)}
                  className="text-sm font-medium transition-colors duration-200 bg-transparent border-none cursor-pointer"
                  style={{ color: 'var(--muted-foreground)' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--foreground)')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--muted-foreground)')}
                >
                  {item.label}
                </button>
              ))}
            </nav>

            {/* CTA */}
            <div className="hidden lg:block">
              <a href={ETSY_URL} target="_blank" rel="noopener noreferrer" className="btn-primary text-sm py-3 px-5">
                Get All 43 Bundles — $79
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width={16} height={16}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                </svg>
              </a>
            </div>

            {/* Mobile Hamburger */}
            <button
              className="lg:hidden p-2 rounded-lg transition-colors"
              style={{ color: 'var(--muted-foreground)' }}
              onClick={() => setMobileOpen(true)}
              aria-label="Open navigation menu"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width={24} height={24}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${mobileOpen ? 'open' : ''}`}>
        <div className="flex items-center justify-between mb-10">
          <a href="/" className="flex items-center gap-2.5">
            <img
              src="/assets/bundlevault-logo.svg"
              alt="BundleVault Logo Icon"
              width={32}
              height={32}
              className="rounded-xl shadow-lg"
            />
            <span className="font-extrabold text-lg" style={{ color: 'var(--foreground)' }}>Premium Bundle Vault</span>
          </a>
          <button
            className="p-2 rounded-lg transition-colors"
            style={{ color: 'var(--muted-foreground)' }}
            onClick={() => setMobileOpen(false)}
            aria-label="Close navigation menu"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width={24} height={24}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <nav className="flex flex-col gap-2 flex-1">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollTo(item.id)}
              className="text-xl font-semibold py-3 text-left bg-transparent border-none cursor-pointer transition-colors"
              style={{ color: 'var(--foreground)' }}
            >
              {item.label}
            </button>
          ))}
        </nav>
        <div className="mt-8">
          <a href={ETSY_URL} target="_blank" rel="noopener noreferrer" className="btn-primary w-full justify-center text-base py-4">
            🚀 Get All 43 Bundles — $79
          </a>
        </div>
      </div>
    </>
  );
}
